import { UserActionsUnion, UserActionTypes } from '../actions/user.actions';
import { User } from '../../user.interfaces';
import { orderByProperty, merge } from 'src/app/shared/utils';

export interface State {
	currentUser: User | null;
	saving: boolean;
	users: User[];
	usersLoading: boolean;
	usersLoaded: boolean;
	error: string | null;
}

export const initialState: State = {
	currentUser: null,
	saving: false,
	users: [],
	usersLoading: false,
	usersLoaded: true,
	error: null,
};

export function reducer(state = initialState, action: UserActionsUnion): State {
	switch (action.type) {
		case UserActionTypes.GetMe: {
			return { ...state };
		}

		case UserActionTypes.GetMeSuccess: {
			return {
				...state,
				currentUser: Array.isArray(action.user) ? action.user [0] : action.user,
			};
		}

		case UserActionTypes.GetMeFailure: {
			return {
				...state,
				currentUser: null,
				error: 'User does not have access to this application',
			};
		}

		case UserActionTypes.GetAllUsers: {
			return {
				...state,
				users: [],
				usersLoading: true,
				usersLoaded: false,
				error: null,
			};
		}

		case UserActionTypes.GetAllUsersSuccess: {
			const users = orderByProperty(action.users, 'lastName');
			return {
				...state,
				users: users,
				usersLoading: false,
				usersLoaded: true,
				error: null,
			};
		}

		case UserActionTypes.GetAllUsersFailure: {
			return {
				...state,
				users: [],
				usersLoading: false,
				usersLoaded: false,
				error: 'Users failed to retrieve',
			};
		}

		case UserActionTypes.AddUser: {
			return {
				...state,
				saving: true,
				error: null,
			};
		}

		case UserActionTypes.AddUserSuccess: {
			let users = [...state.users, action.user];
			users = orderByProperty(users, 'lastName');

			return {
				...state,
				saving: false,
				users: users,
				error: null,
			};
		}

		case UserActionTypes.AddUserFailure: {
			return {
				...state,
				saving: false,
				error: action.error,
			};
		}

		case UserActionTypes.UpdateUser: {
			return {
				...state,
				saving: true,
				error: null,
			};
		}
		
		case UserActionTypes.UpdateUsers: {
			return {
				...state,
				saving: true,
				error: null,
			};
		}

		case UserActionTypes.UpdateUserSuccess: {
			const users = state.users.map(user => {
				if (user.id === action.user.id) {
					return merge(user, action.user);
				} else {
					return user;
				}
			});

			return {
				...state,
				saving: false,
				users: users,
				error: null,
			};
		}
		
		case UserActionTypes.UpdateUsersSuccess: {
			const users = state.users.map(user => {
				const updatedUser = action.users.find(actionUser => actionUser.id == user.id);
				
				if (updatedUser) {
					return merge(user, updatedUser);
				} else {
					return user;
				}
			});

			return {
				...state,
				saving: false,
				users: users,
				error: null,
			};
		}

		case UserActionTypes.UpdateUserFailure: {
			return {
				...state,
				saving: false,
				error: 'Failed to update user',
			};
		}

		case UserActionTypes.DeactivateUser: {
			return {
				...state,
				error: null,
			};
		}

		case UserActionTypes.DeactivateUserSuccess: {
			const users = state.users;
			const userIndex = users.findIndex(user => user.id === action.userId);
			users.splice(userIndex, 1);

			return {
				...state,
				users: users,
				error: null,
			};
		}

		case UserActionTypes.DeactivateUserFailure: {
			return {
				...state,
				error: null,
			};
		}

		case UserActionTypes.SetDefaultSiteForUser: {
			return {
				...state,
				error: null,
			};
		}

		case UserActionTypes.SetDefaultSiteForUserSuccess: {
			return {
				...state,
				currentUser: action.user,
			};
		}

		case UserActionTypes.SetDefaultSiteForUserFailure: {
			return {
				...state,
				error: null,
			};
		}

		case UserActionTypes.AcceptEula: {
			return {
				...state,
				currentUser: { ...state.currentUser, profile: { ...state.currentUser.profile, acceptedEula: true } },
				error: null,
			};
		}

		case UserActionTypes.AcceptEulaSuccess: {
			return {
				...state,
				error: null,
			};
		}

		case UserActionTypes.AcceptEulaFailure: {
			return {
				...state,
				currentUser: { ...state.currentUser, profile: { ...state.currentUser.profile, acceptedEula: false } },
				error: 'Failed to accept EULA',
			};
		}

		default: {
			return state;
		}
	}
}

export const getUser = (state: State) => state.currentUser;

export const getAllUsers = (state: State) => state.users;

export const getUsersLoading = (state: State) => state.usersLoading;

export const getUsersLoaded = (state: State) => state.usersLoaded;

export const getUsersSaving = (state: State) => state.saving;

export const getUserError = (state: State) => state.error;
